import React, { cloneElement, ComponentProps, FC, ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import styled from 'styled-components';
import { IconTwoLine, Item, ItemProps, Text } from '@cld/item';
import Popover from '@cld/popover';
import { StylableComponentProps } from '@cld/common-definitions';
import { asStyledComponent } from '@cld/component-enhancer';
import { ListSelectionProvider, useListSelection } from '../internal/list/ListSelectionProvider';
import { ListVariant, ListVariantProvider, useListVariant } from '../internal/list/ListVariantProvider';
import { useListNavigation } from '../utils/hooks/useListNavigation';
import { useQuerySelectorAll } from '../utils/hooks/useQuerySelectorAll';
import { mergeCallbacks } from '../utils/mergeCallbacks';
import { useUniqueId } from '../utils/hooks/useUniqueId';
import { createChildDataApi } from '../utils/createChildDataApi';

export type Props = {
  overlay: ReactElement;
  children: ReactElement;
  variant?: ListVariant;
} & ComponentProps<typeof Popover>;

export const ID_ATTRIBUTE_NAME = 'data-selectable-id';

interface PopoverChildrenData {
  onClick?: () => void;
  closeOnClick?: boolean;
}
const childDataApi = createChildDataApi<PopoverChildrenData>('popover menu items');
export const useChildRegistry = childDataApi.useChildRegistry;
export const ChildDataRegistryProvider = childDataApi.ChildDataRegistryProvider;
export const useRegisterChildData = childDataApi.useRegisterChildData;

export interface DropdownMenuItemProps extends ItemProps, StylableComponentProps {
  closeOnClick?: boolean;
}

export const useListItemPureProps = ({ closeOnClick = true, onClick, ...rest }: DropdownMenuItemProps) => {
  const listVariant = useListVariant();
  const id = useUniqueId();

  const childData = useMemo(() => ({ onClick, closeOnClick }), [onClick, closeOnClick]);
  useRegisterChildData(id, childData);

  const { onClick: onClickListSelection, ...selection } = useListSelection(id);

  const onClickCombined = mergeCallbacks(onClickListSelection, onClick);
  return { ...rest, ...listVariant, [ID_ATTRIBUTE_NAME]: id, ...selection, onClick: onClickCombined };
};

const DropdownMenuItem = asStyledComponent<FC<DropdownMenuItemProps>>((props) => {
  const listItemProps = useListItemPureProps(props);
  return <Item {...listItemProps} />;
});

const DropdownMenuTextItem = asStyledComponent<FC<DropdownMenuItemProps>>(({ children, ...rest }) => {
  const { ...listItemProps } = useListItemPureProps(rest);
  return (
    <Item {...listItemProps}>
      <Text>{children}</Text>
    </Item>
  );
});

const DropdownMenuIconTwoLineItem = asStyledComponent<
  FC<DropdownMenuItemProps & { icon?: ReactNode; primaryText?: ReactNode; secondaryText?: ReactNode; actions?: ReactNode }>
>(({ children, icon, primaryText, secondaryText, actions, ...rest }) => {
  const { ...listItemProps } = useListItemPureProps(rest);
  return (
    <Item {...listItemProps}>
      <IconTwoLine icon={icon} primaryText={primaryText} secondaryText={secondaryText} actions={actions} />
    </Item>
  );
});

export interface CompoundChildren {
  MenuItem: typeof DropdownMenuItem;
  TextMenuItem: typeof DropdownMenuTextItem;
  IconTwoLineMenuItem: typeof DropdownMenuIconTwoLineItem;
}

const useResetSelection = (visible: boolean, ids: string[], setSelectedId: (value: undefined) => void) => {
  const previousIds = useRef<string[]>([]);
  useEffect(() => {
    if ((visible && isEqual(previousIds.current, ids)) || previousIds.current.length === 0) {
      setSelectedId(undefined);
    }
    previousIds.current = ids;
  }, [ids, visible, setSelectedId]);
};

const ItemContainer = styled.div`
  outline: none;
`;

export const DropdownMenu: CompoundChildren & FC<Props> = ({ children, overlay, variant, ...rest }) => {
  const [visible, setVisible] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const registry = useChildRegistry();

  const { ref: querySelectorRef, result } = useQuerySelectorAll(`[${ID_ATTRIBUTE_NAME}]`);
  const ids = useMemo(() => result.map((element) => String(element.getAttribute(ID_ATTRIBUTE_NAME))), [result]);

  useResetSelection(visible, ids, setSelectedId);

  const getChildData = (id: unknown) => {
    const data = registry.get(id);
    if (!data) {
      throw new Error(`Cannot find data for item of id ${String(id)}. Please remember to use useRegisterChildData() hook in your list item`);
    }

    return data;
  };

  const confirm = () => {
    const childData = getChildData(selectedId);
    // istanbul ignore else
    if (selectedId) {
      childData.onClick?.();
    }
    if (childData.closeOnClick) {
      setVisible(false);
    }
  };
  const cancel = () => {
    setVisible(false);
  };

  const { focusableProps } = useListNavigation(ids, selectedId, setSelectedId, confirm, cancel);

  const onClick = (id: string) => {
    setSelectedId(id);
    if (getChildData(id).closeOnClick) {
      setVisible(false);
    }
  };

  return (
    <>
      <Popover
        paperProps={{ isPadded: false, isRounded: true }}
        visible={visible}
        animation="zoom"
        onVisibleChange={setVisible}
        placement="bottomLeft"
        trigger="click"
        overlay={
          <ChildDataRegistryProvider value={registry}>
            <ListSelectionProvider onClick={onClick} selectedId={selectedId}>
              <ListVariantProvider isRounded {...variant}>
                <ItemContainer {...focusableProps} ref={querySelectorRef}>
                  {overlay}
                </ItemContainer>
              </ListVariantProvider>
            </ListSelectionProvider>
          </ChildDataRegistryProvider>
        }
        {...rest}
      >
        {cloneElement(children, focusableProps)}
      </Popover>
    </>
  );
};

DropdownMenu.MenuItem = DropdownMenuItem;
DropdownMenu.TextMenuItem = DropdownMenuTextItem;
DropdownMenu.IconTwoLineMenuItem = DropdownMenuIconTwoLineItem;
